import React, { useState, useEffect, useMemo } from "react";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import {
  getHrSettings,
  calculateGleitzeit,
  getCorrections,
  saveCorrection,
  deleteCorrection,
  transferYearEnd,
} from "../services/azureHrService";
import {
  getSalaryEntries,
  createSalaryEntry,
  updateSalaryEntry,
  deleteSalaryEntry,
  getPkManualEntry,
  savePkManualEntry,
} from "../services/azureSalaryService";
import {
  createSalaryBudgetEntry,
  create13thMonthBudgetEntries,
  delete13thMonthBudgetEntries,
  updateSalaryBudgetEndDate,
  getBudgetEntryForSalary,
  deleteBudgetEntry,
  saveBudgetEntry,
  recalculateInsuranceBudget,
  recalculateFAKBudget,
} from "../services/azureBudgetService";
import { getAllActiveRates } from "../services/azureInsuranceService";
import { updateTeamMember } from "../services/azureTeamService";
import {
  calcAllDeductions,
  calcFAKZulagen,
  getAgeAtDate,
  getPkManualUpdateReasons,
} from "../utils/insuranceCalculations";

// Ferien-Helper (konsistent mit GleitzeitDisplay / TeamPersonAuswertung)
const isFerienHourlyEntry = (entry) =>
  (entry.typeName || "").toLowerCase().includes("ferien in h");
const isFerienFullDayEntry = (entry) => {
  const t = (entry.typeName || "").toLowerCase();
  if (isFerienHourlyEntry(entry)) return false;
  return (
    entry.isFullDay &&
    (t.includes("ferien") || t.includes("urlaub") || t.includes("vacation"))
  );
};

const TeamDetailModal = ({ member, user, canManageTeam, onClose }) => {
  const [timekeepingData, setTimekeepingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  // HR data
  const [hrSettings, setHrSettings] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [gleitzeitInfo, setGleitzeitInfo] = useState(null);

  // Salary data
  const [salaryEntries, setSalaryEntries] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [salaryBrutto, setSalaryBrutto] = useState("");
  const [salaryNetto, setSalaryNetto] = useState("");
  const [salaryAbDatum, setSalaryAbDatum] = useState("");
  const [salaryPensum, setSalaryPensum] = useState(member.pensum ?? 100);
  const [salaryDreizehnter, setSalaryDreizehnter] = useState(false);
  const [salarySaving, setSalarySaving] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null); // salary entry being edited

  // Correction form (admin only)
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [corrDate, setCorrDate] = useState("");
  const [corrGleitzeit, setCorrGleitzeit] = useState("");
  const [corrHolidays, setCorrHolidays] = useState("");
  const [corrNote, setCorrNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Insurance deduction data
  const [insuranceRates, setInsuranceRates] = useState(null);

  // Manual PK contributions (only relevant when PK rate has berechnungsModus "manuell")
  const [pkManualEntry, setPkManualEntry] = useState(null);
  const [pkAnInput, setPkAnInput] = useState("");
  const [pkAgInput, setPkAgInput] = useState("");
  const [pkSaving, setPkSaving] = useState(false);

  // Check if current user can see salary: GL (canManageTeam) or own profile
  const isOwnProfile = user?.email?.toLowerCase() === member.email?.toLowerCase();
  const canSeeSalary = canManageTeam || isOwnProfile;

  useEffect(() => {
    loadTimekeepingData();
    if (canSeeSalary) {
      loadSalaryData();
      loadInsuranceRates();
      loadPkManualData();
    }
  }, [member]);

  useEffect(() => {
    if (selectedYear) {
      loadHrData();
    }
  }, [selectedYear, timekeepingData, salaryEntries]);

  const loadTimekeepingData = async () => {
    setLoading(true);
    try {
      const data = await getTimekeepingData();
      const memberData = data.filter(
        (entry) => entry.employeeUuid === member.employeeUuid ||
                   entry.employeeName === member.name
      );
      setTimekeepingData(memberData);
      const currentYear = new Date().getFullYear();
      setSelectedYear(currentYear);
    } catch (err) {
      console.error("Fehler beim Laden der Zeitdaten:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSalaryData = async () => {
    setSalaryLoading(true);
    try {
      const entries = await getSalaryEntries(member.employeeUuid);
      setSalaryEntries(entries);
    } catch (err) {
      // Salary load errors are not logged to console per security requirement
    } finally {
      setSalaryLoading(false);
    }
  };

  const loadInsuranceRates = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const rates = await getAllActiveRates(today);
      setInsuranceRates(rates);
    } catch {
      // Insurance rate loading errors silenced
    }
  };

  const loadPkManualData = async () => {
    try {
      const entry = await getPkManualEntry(member.employeeUuid);
      setPkManualEntry(entry);
      setPkAnInput(entry ? String(entry.anBeitrag) : "");
      setPkAgInput(entry ? String(entry.agBeitrag) : "");
    } catch {
      // PK manual loading errors silenced per security
    }
  };

  const loadHrData = async () => {
    try {
      const [settings, corr] = await Promise.all([
        getHrSettings(String(selectedYear)),
        getCorrections(member.teamId, selectedYear).catch(() => []),
      ]);
      setHrSettings(settings);
      setCorrections(corr);

      if (settings) {
        // Calculate hours worked since last correction
        const todayStr = new Date().toISOString().split("T")[0];
        const relevantCorrections = corr.filter((c) => c.date <= todayStr);
        const latestCorrection = relevantCorrections.length > 0
          ? relevantCorrections[relevantCorrections.length - 1]
          : null;

        const calcStart = latestCorrection
          ? (() => { const d = new Date(latestCorrection.date); d.setDate(d.getDate() + 1); return d.toISOString().split("T")[0]; })()
          : `${selectedYear}-01-01`;

        const yearEntries = timekeepingData.filter(
          (e) => e.date && e.date.startsWith(String(selectedYear)) && e.date >= calcStart
        );

        // Ferien aus totalHoursWorked ausschliessen — Credit erfolgt in calculateGleitzeit.
        let totalMinutes = 0;
        yearEntries.forEach((entry) => {
          if (isFerienFullDayEntry(entry) || isFerienHourlyEntry(entry)) return;
          totalMinutes += (entry.durationHours || 0) * 60 + (entry.durationMinutes || 0);
        });

        // Nur vergangene Ferien-Einträge an calculateGleitzeit übergeben
        const pastFerienEntries = yearEntries.filter(
          (entry) =>
            entry.date <= todayStr &&
            (isFerienFullDayEntry(entry) || isFerienHourlyEntry(entry))
        );

        const info = calculateGleitzeit(
          settings,
          member,
          totalMinutes / 60,
          corr,
          pastFerienEntries,
          salaryEntries
        );
        setGleitzeitInfo(info);
      } else {
        setGleitzeitInfo(null);
      }
    } catch (err) {
      console.error("HR data load error:", err);
    }
  };

  // Salary handlers
  const handleEditSalary = (entry) => {
    setEditingSalary(entry);
    setSalaryBrutto(entry.brutto > 0 ? String(entry.brutto) : "");
    setSalaryNetto(entry.netto > 0 ? String(entry.netto) : "");
    setSalaryPensum(entry.pensum ?? member.pensum ?? 100);
    setSalaryAbDatum(entry.abDatum || "");
    setSalaryDreizehnter(entry.dreizehnter || false);
    setShowSalaryForm(true);
  };

  const handleSaveSalary = async () => {
    if ((!salaryBrutto && !salaryNetto) || !salaryAbDatum) return;
    setSalarySaving(true);
    try {
      const bruttoVal = parseFloat(salaryBrutto) || 0;
      const nettoVal = parseFloat(salaryNetto) || 0;
      const pensumVal = parseFloat(salaryPensum) || 100;
      const effektiverBrutto = (bruttoVal * pensumVal) / 100;

      // Budget amount = Nettolohn (after AN insurance deductions)
      // If user entered an explicit netto, use that; otherwise compute via deductions
      let budgetAmount;
      if (nettoVal > 0) {
        budgetAmount = (nettoVal * pensumVal) / 100;
      } else if (insuranceRates && member.geschlecht && member.geburtsdatum) {
        const alter = getAgeAtDate(member.geburtsdatum, new Date());
        const deductions = calcAllDeductions(effektiverBrutto, member.geschlecht, alter, insuranceRates, { pkManuell: pkManualEntry });
        budgetAmount = deductions.netto > 0 ? deductions.netto : effektiverBrutto;
      } else {
        budgetAmount = effektiverBrutto;
      }

      let savedEntry;

      if (editingSalary) {
        // --- UPDATE existing salary entry ---
        savedEntry = await updateSalaryEntry(editingSalary.id, {
          teamMemberId: member.employeeUuid,
          brutto: bruttoVal,
          netto: nettoVal,
          pensum: pensumVal,
          abDatum: salaryAbDatum,
          dreizehnter: salaryDreizehnter,
          createdAt: editingSalary.createdAt,
        });

        // Update the linked budget entry (delete old, create new with updated amount)
        const existingBudget = await getBudgetEntryForSalary(editingSalary.id);
        if (existingBudget) {
          await deleteBudgetEntry(existingBudget.id);
        }
        await delete13thMonthBudgetEntries(editingSalary.id);
      } else {
        // --- CREATE new salary entry ---
        // If there's an existing active entry, close its budget
        const today = new Date().toISOString().split("T")[0];
        const currentActive = salaryEntries.find((e) => e.abDatum <= today);

        savedEntry = await createSalaryEntry({
          teamMemberId: member.employeeUuid,
          brutto: bruttoVal,
          netto: nettoVal,
          pensum: pensumVal,
          abDatum: salaryAbDatum,
          dreizehnter: salaryDreizehnter,
        });

        // Close previous salary budget entry (set bis_wann to day before new entry)
        if (currentActive) {
          const prevDate = new Date(salaryAbDatum);
          prevDate.setDate(prevDate.getDate() - 1);
          const bisWann = prevDate.toISOString().split("T")[0];
          await updateSalaryBudgetEndDate(currentActive.id, bisWann);
          await delete13thMonthBudgetEntries(currentActive.id);
        }
      }

      // Sync pensum to team member record
      if (pensumVal !== (member.pensum ?? 100)) {
        updateTeamMember(member.teamId, { ...member, pensum: pensumVal }).catch(() => {});
      }

      // Budget ab_wann: payments go out on the 26th of each month
      const now = new Date();
      const salaryStart = new Date(salaryAbDatum);
      let payDate = new Date(salaryStart.getFullYear(), salaryStart.getMonth(), 26);
      if (salaryStart.getDate() > 26) payDate.setMonth(payDate.getMonth() + 1);
      if (payDate < now) {
        payDate = new Date(now.getFullYear(), now.getMonth(), 26);
        if (payDate < now) payDate.setMonth(payDate.getMonth() + 1);
      }
      const budgetAbWann = payDate.toISOString().split("T")[0];

      // Create recurring budget entry for this salary
      await createSalaryBudgetEntry({
        teamMemberId: member.employeeUuid,
        salaryEntryId: savedEntry.id,
        memberName: member.name,
        betrag: budgetAmount,
        abWann: budgetAbWann,
        bisWann: member.ende || "",
      });

      // Create 13th month salary budget entries if enabled
      if (salaryDreizehnter) {
        const allEntries = editingSalary
          ? salaryEntries.map((e) => e.id === savedEntry.id ? savedEntry : e).sort((a, b) => a.abDatum > b.abDatum ? -1 : 1)
          : [savedEntry, ...salaryEntries].sort((a, b) => a.abDatum > b.abDatum ? -1 : 1);
        await create13thMonthBudgetEntries({
          teamMemberId: member.employeeUuid,
          salaryEntryId: savedEntry.id,
          memberName: member.name,
          currentAmount: budgetAmount,
          memberStart: member.start,
          allSalaryEntries: allEntries,
          bisWann: member.ende || "",
          memberData: member,
          insuranceRates,
          pkManuell: pkManualEntry,
        });
      }

      // Recalculate aggregated insurance budgets (fire-and-forget) — the
      // service loads the whole team itself (AN+AG over all active members)
      if (insuranceRates) {
        recalculateInsuranceBudget(insuranceRates).catch(() => {});
        if (insuranceRates.fak) {
          recalculateFAKBudget(insuranceRates.fak).catch(() => {});
        }
      }

      // Reset form
      setShowSalaryForm(false);
      setEditingSalary(null);
      setSalaryBrutto("");
      setSalaryNetto("");
      setSalaryPensum(pensumVal);
      setSalaryAbDatum("");
      setSalaryDreizehnter(false);
      await loadSalaryData();
    } catch (err) {
      // Salary errors not logged per security
    } finally {
      setSalarySaving(false);
    }
  };

  const handleDeleteSalary = async (entry) => {
    if (!confirm("Gehaltseintrag und zugehörige Budget-Einträge löschen?")) return;
    try {
      // Delete the linked monthly budget entry
      const budgetEntry = await getBudgetEntryForSalary(entry.id);
      if (budgetEntry) {
        await deleteBudgetEntry(budgetEntry.id);
      }
      // Delete linked 13th month budget entries
      await delete13thMonthBudgetEntries(entry.id);
      // Delete the salary entry
      await deleteSalaryEntry(entry.id);
      await loadSalaryData();
    } catch (err) {
      // Salary errors not logged per security
    }
  };

  // Determine the currently active salary
  const currentSalary = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return salaryEntries.find((e) => e.abDatum <= today) || null;
  }, [salaryEntries]);

  // Calculate insurance deductions for current salary (based on Nettolohn)
  const deductionInfo = useMemo(() => {
    if (!currentSalary || !insuranceRates) return null;
    const lohn = currentSalary.netto || currentSalary.brutto;
    if (!lohn || lohn <= 0) return null;

    const pensum = currentSalary.pensum ?? member.pensum ?? 100;
    const effektiverBrutto = lohn * pensum / 100;
    const geschlecht = member.geschlecht;
    const alter = getAgeAtDate(member.geburtsdatum, new Date());

    return calcAllDeductions(effektiverBrutto, geschlecht, alter, insuranceRates, { pkManuell: pkManualEntry });
  }, [currentSalary, insuranceRates, pkManualEntry, member.pensum, member.geschlecht, member.geburtsdatum]);

  // Manual PK mode: effective pensum + update reasons for this person
  const pkManuellAktiv = insuranceRates?.pk?.berechnungsModus === "manuell";
  const pkEffPensum = currentSalary?.pensum ?? member.pensum ?? 100;
  const pkUpdateReasons = useMemo(() => {
    if (!pkManuellAktiv || !currentSalary) return [];
    return getPkManualUpdateReasons(pkManualEntry, {
      jahr: new Date().getFullYear(),
      pensum: pkEffPensum,
      salaryId: currentSalary.id,
    });
  }, [pkManuellAktiv, pkManualEntry, currentSalary, pkEffPensum]);

  // Save manual PK contributions with context snapshot, then sync budget entries
  const handleSavePkManual = async () => {
    if (pkAnInput === "" || pkAgInput === "") return;
    setPkSaving(true);
    try {
      const saved = await savePkManualEntry(member.employeeUuid, {
        anBeitrag: parseFloat(pkAnInput) || 0,
        agBeitrag: parseFloat(pkAgInput) || 0,
        standJahr: new Date().getFullYear(),
        standPensum: pkEffPensum,
        standSalaryId: currentSalary?.id || "",
      });
      setPkManualEntry(saved);

      // Sync the linked monthly salary budget entry (netto is computed from
      // deductions when no explicit Netto was entered — PK AN changes it)
      if (currentSalary && !(currentSalary.netto > 0) && insuranceRates && member.geschlecht && member.geburtsdatum) {
        const effektiverBrutto = (currentSalary.brutto || 0) * pkEffPensum / 100;
        const alter = getAgeAtDate(member.geburtsdatum, new Date());
        const deductions = calcAllDeductions(effektiverBrutto, member.geschlecht, alter, insuranceRates, { pkManuell: saved });
        const budgetAmount = deductions.netto > 0 ? deductions.netto : effektiverBrutto;
        const budgetEntry = await getBudgetEntryForSalary(currentSalary.id).catch(() => null);
        if (budgetEntry && Math.abs((budgetEntry.betrag || 0) - budgetAmount) > 0.5) {
          await saveBudgetEntry({
            ...budgetEntry,
            betrag: budgetAmount,
            brutto_betrag: budgetAmount,
            netto_betrag: budgetAmount,
          }).catch(() => {});
        }
      }

      // Recalculate aggregated insurance budget (fire-and-forget)
      if (currentSalary && insuranceRates) {
        recalculateInsuranceBudget(insuranceRates).catch(() => {});
      }
    } catch {
      // PK manual save errors silenced per security
    } finally {
      setPkSaving(false);
    }
  };

  // Calculate FAK Familienzulagen for display
  const familienzulagenInfo = useMemo(() => {
    if (!member.kinder?.length || !insuranceRates?.fak) return null;
    return calcFAKZulagen(member.kinder, insuranceRates.fak, new Date());
  }, [member.kinder, insuranceRates]);

  // Group data by year
  const yearlyData = useMemo(() => {
    const years = {};
    timekeepingData.forEach((entry) => {
      if (!entry.date) return;
      const year = entry.date.substring(0, 4);
      if (!years[year]) years[year] = [];
      years[year].push(entry);
    });
    return years;
  }, [timekeepingData]);

  const availableYears = useMemo(() => {
    return Object.keys(yearlyData).sort((a, b) => b - a);
  }, [yearlyData]);

  const getQuarter = (dateStr) => {
    if (!dateStr) return 0;
    const month = parseInt(dateStr.substring(5, 7));
    return Math.ceil(month / 3);
  };

  const quarterlyData = useMemo(() => {
    if (!selectedYear || !yearlyData[selectedYear]) return {};
    const quarters = { 1: [], 2: [], 3: [], 4: [] };
    yearlyData[selectedYear].forEach((entry) => {
      const q = getQuarter(entry.date);
      if (q >= 1 && q <= 4) quarters[q].push(entry);
    });
    return quarters;
  }, [selectedYear, yearlyData]);

  const calculateHours = (entries) => {
    let totalMinutes = 0;
    entries.forEach((entry) => {
      totalMinutes += (entry.durationHours || 0) * 60 + (entry.durationMinutes || 0);
    });
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
      decimal: (totalMinutes / 60).toFixed(1),
    };
  };

  const getEntryCategory = (entry) => {
    if (entry.code && entry.code.length >= 2) {
      return { type: "project", key: entry.code, name: entry.contractNoteName || entry.code };
    }
    const category = entry.typeName || entry.contractNoteName || "Sonstiges";
    return { type: "internal", key: category, name: category };
  };

  const groupEntries = (entries) => {
    const projects = {};
    const internal = {};
    entries.forEach((entry) => {
      const cat = getEntryCategory(entry);
      if (cat.type === "project") {
        if (!projects[cat.key]) projects[cat.key] = { name: cat.name, entries: [] };
        projects[cat.key].entries.push(entry);
      } else {
        if (!internal[cat.key]) internal[cat.key] = { name: cat.name, entries: [] };
        internal[cat.key].entries.push(entry);
      }
    });

    const projectList = Object.entries(projects)
      .map(([code, data]) => ({ code, name: data.name, ...calculateHours(data.entries), count: data.entries.length }))
      .sort((a, b) => parseFloat(b.decimal) - parseFloat(a.decimal));

    const internalList = Object.entries(internal)
      .map(([code, data]) => ({ code, name: data.name, ...calculateHours(data.entries), count: data.entries.length }))
      .sort((a, b) => parseFloat(b.decimal) - parseFloat(a.decimal));

    return { projectList, internalList };
  };

  const displayEntries = useMemo(() => {
    if (!selectedYear || !yearlyData[selectedYear]) return [];
    if (selectedQuarter && quarterlyData[selectedQuarter]) return quarterlyData[selectedQuarter];
    return yearlyData[selectedYear];
  }, [selectedYear, selectedQuarter, yearlyData, quarterlyData]);

  const { projectList, internalList } = useMemo(() => groupEntries(displayEntries), [displayEntries]);
  const totalHours = useMemo(() => calculateHours(displayEntries), [displayEntries]);

  // Correction handlers
  const handleSaveCorrection = async () => {
    if (!corrDate) return;
    setSaving(true);
    try {
      await saveCorrection(
        member.teamId,
        corrDate,
        parseFloat(corrGleitzeit) || 0,
        parseFloat(corrHolidays) || 0,
        corrNote
      );
      setShowCorrectionForm(false);
      setCorrDate("");
      setCorrGleitzeit("");
      setCorrHolidays("");
      setCorrNote("");
      await loadHrData();
    } catch (err) {
      console.error("Correction save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCorrection = async (date) => {
    if (!confirm("Korrektur löschen?")) return;
    try {
      await deleteCorrection(member.teamId, date);
      await loadHrData();
    } catch (err) {
      console.error("Correction delete error:", err);
    }
  };

  const handleYearEndTransfer = async () => {
    if (!gleitzeitInfo) return;
    if (!confirm(`Übertrag nach ${selectedYear + 1}?\nGleitzeit: ${gleitzeitInfo.gleitzeit}h\nFerientage: ${gleitzeitInfo.holidaysRemaining} Tage`)) return;
    try {
      await transferYearEnd(
        member.teamId,
        selectedYear,
        parseFloat(gleitzeitInfo.gleitzeit),
        parseFloat(gleitzeitInfo.holidaysRemaining)
      );
      await loadHrData();
    } catch (err) {
      console.error("Year-end transfer error:", err);
    }
  };

  const formatCHF = (value) => {
    return new Intl.NumberFormat("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="team-detail-modal">
        <div className="modal-header">
          <div className="member-header-info">
            {member.bildUrls?.thumbnail && (
              <img
                src={member.bildUrls.medium || member.bildUrls.thumbnail}
                alt={member.name}
                className="member-avatar"
              />
            )}
            <div>
              <h2>{member.name}</h2>
              <div className="member-meta">
                {member.kategorie && (
                  <span className="category-badge-detail">{member.kategorie}</span>
                )}
                <span>{member.nummer}</span>
                {member.pensum && <span>{member.pensum}%</span>}
              </div>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* Current State Summary */}
          {gleitzeitInfo && (
            <div className="current-state-section">
              <div className="state-grid">
                <div className="state-item">
                  <label>Tagesarbeitszeit</label>
                  <span>{gleitzeitInfo.taeglicheArbeitszeit}h</span>
                </div>
                <div className="state-item">
                  <label>Gleitzeit</label>
                  <span className={parseFloat(gleitzeitInfo.gleitzeit) < 0 ? "negative" : "positive"}>
                    {parseFloat(gleitzeitInfo.gleitzeit) >= 0 ? "+" : ""}{gleitzeitInfo.gleitzeit}h
                  </span>
                </div>
                {parseFloat(gleitzeitInfo.holidayEntitlement) > 0 && (
                  <div className="state-item">
                    <label>Ferientage</label>
                    <span>{gleitzeitInfo.holidaysTaken}/{gleitzeitInfo.holidayEntitlement}</span>
                  </div>
                )}
                <div className="state-item">
                  <label>Jahresstunden</label>
                  <span>{gleitzeitInfo.personalJahresarbeitszeit}h</span>
                </div>
              </div>
            </div>
          )}

          {/* Member Info */}
          <div className="detail-section">
            <div className="detail-grid">
              <div className="detail-item">
                <label>Start</label>
                <span>{member.start || "-"}</span>
              </div>
              <div className="detail-item">
                <label>Ende</label>
                <span>{member.ende || "-"}</span>
              </div>
              {canManageTeam && (
                <div className="detail-item">
                  <label>Stundensatz</label>
                  <span>{member.internalRate || 0} CHF/h</span>
                </div>
              )}
            </div>
          </div>

          {/* Salary Section (GL or own profile only) */}
          {canSeeSalary && (
            <div className="salary-section">
              <div className="salary-header">
                <h3>Lohn</h3>
                {canManageTeam && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      if (showSalaryForm) {
                        setShowSalaryForm(false);
                        setEditingSalary(null);
                        setSalaryBrutto("");
                        setSalaryNetto("");
                        setSalaryPensum(member.pensum ?? 100);
                        setSalaryAbDatum("");
                        setSalaryDreizehnter(false);
                      } else {
                        setEditingSalary(null);
                        setShowSalaryForm(true);
                      }
                    }}
                  >
                    {showSalaryForm ? "Abbrechen" : "+ Lohneintrag"}
                  </button>
                )}
              </div>

              {/* Salary Add Form (Admin only) */}
              {canManageTeam && showSalaryForm && (
                <div className="salary-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Bruttolohn (CHF)</label>
                      <input
                        type="number"
                        value={salaryBrutto}
                        onChange={(e) => setSalaryBrutto(e.target.value)}
                        placeholder="z.B. 6500"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nettolohn (CHF)</label>
                      <input
                        type="number"
                        value={salaryNetto}
                        onChange={(e) => setSalaryNetto(e.target.value)}
                        placeholder="z.B. 5200"
                      />
                    </div>
                    <div className="form-group">
                      <label>Pensum (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={salaryPensum}
                        onChange={(e) => setSalaryPensum(parseFloat(e.target.value) || 0)}
                        placeholder="z.B. 80"
                      />
                    </div>
                    <div className="form-group">
                      <label>Ab Datum</label>
                      <input
                        type="date"
                        value={salaryAbDatum}
                        onChange={(e) => setSalaryAbDatum(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row salary-form-actions">
                    <label className="checkbox-label dreizehnter-toggle">
                      <input
                        type="checkbox"
                        checked={salaryDreizehnter}
                        onChange={(e) => setSalaryDreizehnter(e.target.checked)}
                      />
                      13. Monatslohn
                    </label>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleSaveSalary}
                      disabled={salarySaving || (!salaryBrutto && !salaryNetto) || !salaryAbDatum}
                    >
                      {salarySaving ? "..." : editingSalary ? "Aktualisieren" : "Speichern"}
                    </button>
                  </div>
                </div>
              )}

              {/* Salary History */}
              {salaryLoading ? (
                <div className="loading-indicator">Lade Lohndaten...</div>
              ) : salaryEntries.length > 0 ? (
                <div className="salary-history">
                  {salaryEntries.map((entry) => {
                    const isCurrent = currentSalary?.id === entry.id;
                    return (
                      <div key={entry.id} className={`salary-entry${isCurrent ? " salary-current" : ""}`}>
                        <div className="salary-entry-main">
                          <span className="salary-date">{entry.abDatum}</span>
                          {entry.brutto > 0 && (
                            <span className="salary-value">
                              <span className="salary-label">Brutto</span>
                              {formatCHF(entry.brutto)} CHF
                            </span>
                          )}
                          {entry.brutto > 0 && entry.pensum != null && entry.pensum !== 100 && (
                            <span className="salary-value salary-value-computed">
                              <span className="salary-label">Effektiv</span>
                              {formatCHF(entry.brutto * entry.pensum / 100)} CHF
                            </span>
                          )}
                          {entry.netto > 0 && (
                            <span className="salary-value">
                              <span className="salary-label">Netto</span>
                              {formatCHF(entry.netto)} CHF
                            </span>
                          )}
                          {deductionInfo && isCurrent && (
                            <span className="salary-value salary-value-computed">
                              <span className="salary-label">Berechnet</span>
                              {formatCHF(deductionInfo.netto)} CHF
                            </span>
                          )}
                          {entry.dreizehnter && (
                            <span className="salary-badge">13.</span>
                          )}
                          {entry.pensum != null && (
                            <span className="salary-badge">{entry.pensum}%</span>
                          )}
                        </div>
                        {canManageTeam && (
                          <div className="salary-entry-actions">
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEditSalary(entry)}
                              title="Bearbeiten"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteSalary(entry)}
                              title="Löschen"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data-message">Keine Lohneinträge vorhanden</div>
              )}

              {/* Manual PK contributions (GL only, when PK mode is "manuell") */}
              {pkManuellAktiv && canManageTeam && (
                <div className={`pk-manual-section${pkUpdateReasons.length > 0 ? " pk-manual-stale" : ""}`}>
                  <h4>Pensionskasse (manuell)</h4>
                  {pkUpdateReasons.length > 0 && (
                    <div className="pk-manual-hint">
                      Aktualisierung nötig: {pkUpdateReasons.join(", ")}
                    </div>
                  )}
                  {!currentSalary && (
                    <div className="pk-manual-hint">
                      Kein aktiver Lohneintrag — PK-Beiträge können erst nach Erfassung eines Lohns gespeichert werden.
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>MA-Beitrag (CHF/Mt.)</label>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        value={pkAnInput}
                        onChange={(e) => setPkAnInput(e.target.value)}
                        placeholder="z.B. 320.50"
                      />
                    </div>
                    <div className="form-group">
                      <label>AG-Beitrag (CHF/Mt.)</label>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        value={pkAgInput}
                        onChange={(e) => setPkAgInput(e.target.value)}
                        placeholder="z.B. 480.75"
                      />
                    </div>
                    <div className="form-group pk-manual-save">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSavePkManual}
                        disabled={pkSaving || !currentSalary || pkAnInput === "" || pkAgInput === ""}
                      >
                        {pkSaving ? "..." : "Bestätigen"}
                      </button>
                    </div>
                  </div>
                  <div className="pk-manual-meta">
                    {pkManualEntry
                      ? `Zuletzt bestätigt: ${(pkManualEntry.updatedAt || "").split("T")[0] || "-"} (Jahr ${pkManualEntry.standJahr}, Pensum ${pkManualEntry.standPensum}%)`
                      : "Noch keine Beiträge erfasst."}
                  </div>
                </div>
              )}

              {/* AN Deduction Breakdown */}
              {deductionInfo && deductionInfo.deductions.length > 0 && (
                <div className="deduction-breakdown">
                  <h4>Abzüge (AN) - {currentSalary?.pensum ?? member.pensum ?? 100}% Pensum</h4>
                  {(!member.geschlecht || !member.geburtsdatum) && (
                    <div className="deduction-hint">
                      Geschlecht oder Geburtsdatum fehlt — SUVA, KTG und PK können nicht berechnet werden.
                    </div>
                  )}
                  <table className="deduction-table">
                    <tbody>
                      {deductionInfo.deductions.map((d) => (
                        <tr key={d.type}>
                          <td className="deduction-label">{d.label}</td>
                          <td className="deduction-value">-{formatCHF(d.anAbzug)} CHF</td>
                        </tr>
                      ))}
                      <tr className="deduction-total-row">
                        <td className="deduction-label">Total Abzüge</td>
                        <td className="deduction-value">-{formatCHF(deductionInfo.totalAN)} CHF</td>
                      </tr>
                      <tr className="deduction-netto-row">
                        <td className="deduction-label">Netto (berechnet)</td>
                        <td className="deduction-value">{formatCHF(deductionInfo.netto)} CHF</td>
                      </tr>
                    </tbody>
                  </table>
                  {deductionInfo.totalAG > 0 && (
                    <div className="ag-costs-hint">
                      AG-Kosten: {formatCHF(deductionInfo.totalAG)} CHF/Mt.
                    </div>
                  )}
                </div>
              )}

              {/* Familienzulagen Section */}
              {familienzulagenInfo && member.kinder?.length > 0 && (
                <div className="familienzulagen-section">
                  <h4>Familienzulagen</h4>
                  <div className="familienzulagen-list">
                    {member.kinder.map((kind, idx) => {
                      const alter = getAgeAtDate(kind.geburtsdatum, new Date());
                      let zulageType = null;
                      let zulageBetrag = 0;
                      if (alter != null && alter >= 0 && alter < 16) {
                        zulageType = "Kinderzulage";
                        zulageBetrag = insuranceRates?.fak?.kinderzulage || 0;
                      } else if (alter != null && alter >= 16 && alter <= 25 && kind.inAusbildung) {
                        zulageType = "Ausbildungszulage";
                        zulageBetrag = insuranceRates?.fak?.ausbildungszulage || 0;
                      }
                      return (
                        <div key={idx} className="kind-zulage-item">
                          <span className="kind-name">{kind.vorname || `Kind ${idx + 1}`}</span>
                          <span className="kind-alter">{alter != null ? `${alter} J.` : "-"}</span>
                          {zulageType ? (
                            <span className="kind-zulage">{zulageType}: {formatCHF(zulageBetrag)} CHF</span>
                          ) : (
                            <span className="kind-zulage kein-anspruch">Kein Anspruch</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {familienzulagenInfo.totalZulagen > 0 && (
                    <div className="familienzulagen-total">
                      Total Zulagen: {formatCHF(familienzulagenInfo.totalZulagen)} CHF/Mt.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Corrections Section (Admin only) */}
          {canManageTeam && (
            <div className="corrections-section">
              <div className="corrections-header">
                <h3>Korrekturen</h3>
                <div className="corrections-actions">
                  {gleitzeitInfo && selectedYear < new Date().getFullYear() && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleYearEndTransfer}
                      title={`Übertrag nach ${selectedYear + 1}`}
                    >
                      Übertrag {selectedYear + 1}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setShowCorrectionForm(!showCorrectionForm)}
                  >
                    {showCorrectionForm ? "Abbrechen" : "+ Korrektur"}
                  </button>
                </div>
              </div>

              {showCorrectionForm && (
                <div className="correction-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Datum</label>
                      <input
                        type="date"
                        value={corrDate}
                        onChange={(e) => setCorrDate(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Gleitzeit (h)</label>
                      <input
                        type="number"
                        step="0.5"
                        value={corrGleitzeit}
                        onChange={(e) => setCorrGleitzeit(e.target.value)}
                        placeholder="z.B. 12.5"
                      />
                    </div>
                    <div className="form-group">
                      <label>Ferientage</label>
                      <input
                        type="number"
                        step="0.5"
                        value={corrHolidays}
                        onChange={(e) => setCorrHolidays(e.target.value)}
                        placeholder="z.B. -2.0"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Notiz</label>
                      <input
                        type="text"
                        value={corrNote}
                        onChange={(e) => setCorrNote(e.target.value)}
                        placeholder="z.B. Korrektur Q1"
                      />
                    </div>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleSaveCorrection}
                      disabled={saving || !corrDate}
                    >
                      {saving ? "..." : "Speichern"}
                    </button>
                  </div>
                </div>
              )}

              {corrections.length > 0 && (
                <div className="corrections-list">
                  {corrections.map((c) => (
                    <div key={c.date} className="correction-item">
                      <span className="corr-date">{c.date}</span>
                      <span className="corr-values">
                        GL: {c.gleitzeitHours >= 0 ? "+" : ""}{c.gleitzeitHours}h
                        {c.holidayDays ? ` | FT: ${c.holidayDays >= 0 ? "+" : ""}${c.holidayDays}` : ""}
                      </span>
                      {c.note && <span className="corr-note">{c.note}</span>}
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteCorrection(c.date)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hours Overview */}
          <div className="hours-section">
            <h3>Stundenübersicht</h3>

            {loading ? (
              <div className="loading-indicator">Lade Zeitdaten...</div>
            ) : (
              <>
                <div className="year-tabs">
                  {availableYears.map((year) => (
                    <button
                      key={year}
                      className={`tab-btn ${selectedYear === parseInt(year) ? "active" : ""}`}
                      onClick={() => { setSelectedYear(parseInt(year)); setSelectedQuarter(null); }}
                    >
                      {year}
                    </button>
                  ))}
                  {availableYears.length === 0 && (
                    <span className="no-data-hint">Keine Zeitdaten vorhanden</span>
                  )}
                </div>

                {selectedYear && (
                  <div className="quarter-tabs">
                    <button
                      className={`tab-btn tab-btn-sm ${!selectedQuarter ? "active" : ""}`}
                      onClick={() => setSelectedQuarter(null)}
                    >
                      Gesamt
                    </button>
                    {[1, 2, 3, 4].map((q) => (
                      <button
                        key={q}
                        className={`tab-btn tab-btn-sm ${selectedQuarter === q ? "active" : ""}`}
                        onClick={() => setSelectedQuarter(q)}
                      >
                        Q{q}
                      </button>
                    ))}
                  </div>
                )}

                {displayEntries.length > 0 && (
                  <div className="hours-total">
                    <span className="total-label">
                      Total {selectedQuarter ? `Q${selectedQuarter}` : ""} {selectedYear}:
                    </span>
                    <span className="total-value">{totalHours.decimal}h</span>
                    <span className="total-detail">({totalHours.hours}h {totalHours.minutes}min)</span>
                  </div>
                )}

                {projectList.length > 0 && (
                  <div className="project-breakdown">
                    <h4 className="breakdown-title">Projekte</h4>
                    <table className="hours-table">
                      <thead>
                        <tr><th>Projekt</th><th>Code</th><th>Stunden</th><th>Einträge</th></tr>
                      </thead>
                      <tbody>
                        {projectList.map((project) => (
                          <tr key={project.code}>
                            <td className="project-name-cell">{project.name}</td>
                            <td className="project-code-cell">{project.code}</td>
                            <td className="hours-cell">{project.decimal}h</td>
                            <td className="count-cell">{project.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {internalList.length > 0 && (
                  <div className="project-breakdown internal-breakdown">
                    <h4 className="breakdown-title">Intern</h4>
                    <table className="hours-table">
                      <thead>
                        <tr><th>Kategorie</th><th></th><th>Stunden</th><th>Einträge</th></tr>
                      </thead>
                      <tbody>
                        {internalList.map((item) => (
                          <tr key={item.code}>
                            <td className="project-name-cell">{item.name}</td>
                            <td></td>
                            <td className="hours-cell">{item.decimal}h</td>
                            <td className="count-cell">{item.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {displayEntries.length === 0 && selectedYear && (
                  <div className="no-data-message">
                    Keine Einträge fuer {selectedQuarter ? `Q${selectedQuarter} ` : ""}{selectedYear}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamDetailModal;
